/**
 * Backend-sync facade for transform pipelines and builders.
 *
 * Wraps the structured useQueries primitives with the right wiring into
 * queryBuilderStore — saving a pipeline pushes the body+sidecar pair, loading
 * hydrates the store, and the one-shot migration sweep promotes any
 * localStorage-only pipelines/builders to backend records on first project
 * visit post-feature.
 *
 * Lives outside the store because Zustand actions can't depend on React
 * hooks; this layer reads/writes the store via getState/imported actions.
 */
import { useCallback } from "react"
import {
  useQueryBuilderStore,
  buildTransformPrefix,
  TransformPipeline,
  BuilderTab,
} from "stores/queryBuilderStore"
import {
  EnvelopeMeta,
  PipelineMeta,
  BuilderMeta,
  SavedQueryEnvelope,
} from "types/savedQuery.types"
import { useSavedQueries } from "features/QueryConsole/api/useSavedQueries"
import ScriptGenerationService from "services/ScriptGenerationService"

const sanitizeIdent = (s: string): string =>
  s.replace(/[^a-zA-Z0-9_]/g, "_").substring(0, 24) || "builder"

/**
 * Build the canonical Data(Q) script that represents a builder's intended run.
 * Mirrors `useBuilderScript` but without the active-builder hook bias —
 * needed when saving an arbitrary builder by id.
 */
const builderScriptOf = (
  builder: BuilderTab,
  pipeline: TransformPipeline | null
): string => {
  if (
    builder.rowVariables.length === 0 ||
    builder.columnVariables.length === 0
  ) {
    return "" // not runnable yet — caller can still save the meta-only record
  }
  const transformPrefix = pipeline
    ? buildTransformPrefix(pipeline.steps)
    : ""
  let script = ScriptGenerationService.generateComplexScript({
    tableId: sanitizeIdent(builder.name),
    explicitOutputId: builder.outputName,
    rowVariables: builder.rowVariables,
    columnVariables: builder.columnVariables,
    selectedOperators: builder.selectedOperators,
    tableOperationType: builder.operation,
    filter: builder.universeFilter || undefined,
  })
  if (transformPrefix) {
    script = script.replace(/^(RESET\n)/, `$1${transformPrefix}\n`)
  }
  if (builder.isTransposed) {
    script = script.replace(/^OUTPUT (\S+)\s*$/m, "OUTPUT $1 /T")
  }
  return script
}

interface UseTransformBackend {
  /** Push a pipeline to the backend under the given saved-query name. */
  savePipeline: (
    projectId: string,
    owner: string,
    pipelineId: string,
    saveName: string
  ) => Promise<void>
  /** Push a builder to the backend under the given saved-query name. */
  saveBuilder: (
    projectId: string,
    owner: string,
    builderId: string,
    saveName: string
  ) => Promise<void>
  /**
   * Load every structured envelope for the project and merge into the store.
   * Triggers the one-shot migration sweep on first call after the v1 cutover.
   */
  hydrateFromBackend: (projectId: string, owner: string) => Promise<void>
}

export function useTransformBackend(): UseTransformBackend {
  const { saveStructured, listStructured, deleteQuery } = useSavedQueries()

  const savePipeline = useCallback(
    async (
      projectId: string,
      owner: string,
      pipelineId: string,
      saveName: string
    ): Promise<void> => {
      const state = useQueryBuilderStore.getState()
      const pipeline = state.transformPipelines.find(
        (p) => p.id === pipelineId
      )
      if (!pipeline) return
      // Capture the previous saved name BEFORE we mutate the store — this is
      // what the backend has on disk under the old identity, and we'll need
      // to delete it if the user has renamed.
      const previousSavedName = state.pipelineSavedNames[pipelineId]
      // Persist under `saveName`; if this differs from the in-store name, also
      // rename the local pipeline so subsequent saves are idempotent.
      if (pipeline.name !== saveName) {
        state.renameTransformPipeline(pipelineId, saveName)
      }
      const persistedPipeline: TransformPipeline = {
        ...pipeline,
        name: saveName,
      }
      const script = buildTransformPrefix(persistedPipeline.steps)
      const meta: PipelineMeta = {
        kind: "pipeline",
        pipeline: persistedPipeline,
      }
      // The body for a pipeline-only record is just the prefix script. Empty
      // pipelines emit an empty body — Repos accepts it.
      await saveStructured(
        projectId,
        owner,
        saveName,
        script || "# (empty pipeline)\n",
        meta
      )
      useQueryBuilderStore.getState().markPipelineSaved(pipelineId, saveName)
      // Drop the old backend record if this was a rename. Best-effort —
      // a transient delete failure here means an orphan, not data loss,
      // and the next rename-and-save will retry.
      if (previousSavedName && previousSavedName !== saveName) {
        try {
          await deleteQuery(projectId, owner, previousSavedName)
        } catch (err) {
          console.warn(
            "Failed to delete previous saved pipeline",
            previousSavedName,
            err
          )
        }
      }
    },
    [saveStructured, deleteQuery]
  )

  const saveBuilder = useCallback(
    async (
      projectId: string,
      owner: string,
      builderId: string,
      saveName: string
    ): Promise<void> => {
      const state = useQueryBuilderStore.getState()
      const builder = state.builders.find((b) => b.id === builderId)
      if (!builder) return
      const previousSavedName = state.builderSavedNames[builderId]
      // Same self-rename pattern as savePipeline — keeps subsequent saves
      // pushing under the canonical name.
      if (builder.name !== saveName) {
        state.renameBuilder(builderId, saveName)
      }
      const persistedBuilder: BuilderTab = {
        ...builder,
        name: saveName,
      }
      const pipeline = persistedBuilder.transformPipelineId
        ? state.transformPipelines.find(
            (p) => p.id === persistedBuilder.transformPipelineId
          ) ?? null
        : null
      const script = builderScriptOf(persistedBuilder, pipeline)
      // Strip the local pipeline id from the on-wire shape; cross-device loads
      // rebind by name via `applyHydratedBuilders`.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { transformPipelineId: _drop, ...builderForWire } = persistedBuilder
      const meta: BuilderMeta = {
        kind: "builder",
        builder: builderForWire,
        pipelineName: pipeline?.name ?? null,
      }
      await saveStructured(
        projectId,
        owner,
        saveName,
        script || "# (incomplete builder)\n",
        meta
      )
      useQueryBuilderStore.getState().markBuilderSaved(builderId, saveName)
      if (previousSavedName && previousSavedName !== saveName) {
        try {
          await deleteQuery(projectId, owner, previousSavedName)
        } catch (err) {
          console.warn(
            "Failed to delete previous saved builder",
            previousSavedName,
            err
          )
        }
      }
    },
    [saveStructured, deleteQuery]
  )

  const hydrateFromBackend = useCallback(
    async (projectId: string, owner: string): Promise<void> => {
      const envelopes = await listStructured(projectId, owner)
      const pipelines = filterPipelineEnvelopes(envelopes)
      const builders = filterBuilderEnvelopes(envelopes)
      const state = useQueryBuilderStore.getState()

      // One-shot migration: any local pipeline/builder not present on the
      // backend gets pushed up. Runs once per project (gated by migratedV1).
      if (!state.migratedV1) {
        const remotePipelineNames = new Set(pipelines.map((p) => p.name))
        for (const local of state.transformPipelines) {
          if (!remotePipelineNames.has(local.name)) {
            try {
              await savePipeline(projectId, owner, local.id, local.name)
            } catch (err) {
              console.warn(
                "Migration: failed to save pipeline",
                local.name,
                err
              )
            }
          }
        }
        const remoteBuilderNames = new Set(builders.map((b) => b.builder.name))
        for (const local of state.builders) {
          if (!remoteBuilderNames.has(local.name)) {
            try {
              await saveBuilder(projectId, owner, local.id, local.name)
            } catch (err) {
              console.warn(
                "Migration: failed to save builder",
                local.name,
                err
              )
            }
          }
        }
        useQueryBuilderStore.getState().markMigrated()
      }

      // Hydrate the store with the backend's view (including anything we just
      // migrated up — they'll round-trip through name matching).
      const refreshed = await listStructured(projectId, owner)
      useQueryBuilderStore
        .getState()
        .applyHydratedPipelines(filterPipelineEnvelopes(refreshed))
      useQueryBuilderStore
        .getState()
        .applyHydratedBuilders(filterBuilderEnvelopes(refreshed))
    },
    [listStructured, savePipeline, saveBuilder]
  )

  return { savePipeline, saveBuilder, hydrateFromBackend }
}

function filterPipelineEnvelopes(
  envelopes: SavedQueryEnvelope[]
): TransformPipeline[] {
  const out: TransformPipeline[] = []
  for (const env of envelopes) {
    if (env.kind === "pipeline" && env.meta.kind === "pipeline") {
      out.push({ ...env.meta.pipeline, name: env.name })
    }
  }
  return out
}

function filterBuilderEnvelopes(
  envelopes: SavedQueryEnvelope[]
): Array<{
  builder: Omit<BuilderTab, "transformPipelineId">
  pipelineName: string | null
}> {
  const out: Array<{
    builder: Omit<BuilderTab, "transformPipelineId">
    pipelineName: string | null
  }> = []
  for (const env of envelopes) {
    if (env.kind === "builder" && env.meta.kind === "builder") {
      out.push({
        builder: { ...env.meta.builder, name: env.name },
        pipelineName: env.meta.pipelineName,
      })
    }
  }
  return out
}

// Keep used internally so the ESLint unused-import check stays quiet when
// callers don't reference `EnvelopeMeta` directly.
export type { EnvelopeMeta }
